/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import styled from 'styled-components';
import { TerminalDetails } from '../../../../shared/entities/terminal';
import { DetailsPanelGroupCollapseState } from '../../../../store/ui/reducer';
import { isOnline } from '../../../../utils/ConnectionStatusUtils';
import { bytesToUnitString } from '../../../../utils/DataAmountUtils';
import { getTerminalTypeString, valueOrPlaceholder } from '../../../../utils/StringUtils';
import { getTxPowerDbDisplayValue } from '../../../../utils/TerminalUtilities';
import CollapseGroup from '../../../common/collapse_group/CollapseGroup';
import CollapseGroupItem from '../../../common/collapse_group/CollapseGroupItem';
import ConnectivityCollapseGroupItem from '../../../common/collapse_group/ConnectivityCollapseGroupItem';
import { calculateScrollbarHeightOffset, CollapseGroupScrollbarContainer } from './TerminalTabPanel';
import CollapseGroupItemTerminalStatus from './shared/CollapseGroupItemTerminalStatus';

interface TerminalTabDataLinkwayProps extends TerminalTabDataLinkwayStateProps, TerminalTabDataLinkwayDispatchProps {}

export interface TerminalTabDataLinkwayStateProps {
  hidden: boolean;
  tailDetailsPanelTerminal?: TerminalDetails;
  detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState;
}

export interface TerminalTabDataLinkwayDispatchProps {
  setDetailsPanelGroupCollapseState: (detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) => {};
}

const MODEM_TOOLTIP_MAX_WIDTH_PX = 124;

const StartSpacer = styled.div`
  height: 20px;
`;

const GroupDivider = styled.div`
  height: 15px;
`;

const TerminalTabDataLinkway = ({
  hidden,
  tailDetailsPanelTerminal,
  detailsPanelGroupCollapseState,
  setDetailsPanelGroupCollapseState,
}: TerminalTabDataLinkwayProps) => {
  function toggleGroupCollapsed(groupName: keyof DetailsPanelGroupCollapseState) {
    setDetailsPanelGroupCollapseState({
      ...detailsPanelGroupCollapseState,
      [groupName]: !detailsPanelGroupCollapseState[groupName],
    });
  }

  let isOffline = !isOnline((tailDetailsPanelTerminal || {}).connectivityStatus);

  const scrollbarHeightOffset = calculateScrollbarHeightOffset(tailDetailsPanelTerminal);

  return !hidden ? (
    <CollapseGroupScrollbarContainer scrollbarHeightOffset={scrollbarHeightOffset}>
      <StartSpacer />
      <CollapseGroup
        data-test="terminal__status__collapse__group__header--current__activity"
        collapseButtonClicked={() => {
          toggleGroupCollapsed('currentActivityGroupOpened');
        }}
        open={detailsPanelGroupCollapseState.currentActivityGroupOpened}
        title="Activity"
      >
        <div className={isOffline ? 'tail__details_faded_wrapper' : ''}>
          <ConnectivityCollapseGroupItem terminal={tailDetailsPanelTerminal} />
          <CollapseGroupItem
            id="lw_tx_power"
            label="TX Power"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwTxPowerDb : null}
            valueFormatter={getTxPowerDbDisplayValue}
          />
          <CollapseGroupItem
            id="fl_data_use"
            label="FL Data Use"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.flDataUseBytes : null}
            valueFormatter={bytesToUnitString}
          />
          <CollapseGroupItem
            id="rl_data_use"
            label="RL Data Use"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.rlDataUseBytes : null}
            valueFormatter={bytesToUnitString}
          />
        </div>
      </CollapseGroup>
      <CollapseGroupItemTerminalStatus
        isOffline={isOffline}
        tailDetailsPanelTerminal={tailDetailsPanelTerminal}
        detailsPanelGroupCollapseState={detailsPanelGroupCollapseState}
        toggleGroupCollapsed={toggleGroupCollapsed}
      />
      <CollapseGroup
        data-test="terminal__status__collapse__group__header--tail__configuration"
        collapseButtonClicked={() => {
          toggleGroupCollapsed('tailConfigurationGroupOpened');
        }}
        open={detailsPanelGroupCollapseState.tailConfigurationGroupOpened}
        title="Terminal Configuration"
      >
        <div className={isOffline ? 'tail__details_faded_wrapper' : ''}>
          <CollapseGroupItem
            id="terminalType"
            label="Terminal Type"
            value={getTerminalTypeString(tailDetailsPanelTerminal)}
          />
          <CollapseGroupItem
            id="modem"
            label="Modem"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.modemType : null}
            truncate
            tooltipMaxWidth={MODEM_TOOLTIP_MAX_WIDTH_PX}
          />
          <CollapseGroupItem
            id="terminalSerialNumber"
            label="Terminal Serial Number"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.aircraftSerialNumber : null}
          />
          <CollapseGroupItem
            id="macAddress"
            label="MAC Address"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.mtMacAddr : null}
          />
          <GroupDivider />
          <CollapseGroupItem
            id="lwSiteName"
            label="Site Name"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwSiteName : null}
          />
          <CollapseGroupItem
            id="lwSiteId"
            label="Site ID"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwSiteId : null}
          />
          <CollapseGroupItem
            id="lwControlGroup"
            label="Control Group"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwControlGroup : null}
          />
          <GroupDivider />
          <CollapseGroupItem
            id="lwBucLoFrequency"
            label="LO Frequency (BUC)"
            value={valueOrPlaceholder(tailDetailsPanelTerminal?.lwBucLoFrequencyKHz, (value: string) => value + ' kHz')}
          />
          <CollapseGroupItem
            id="lwLnbLoFrequency"
            label="LO Frequency (LNB)"
            value={valueOrPlaceholder(tailDetailsPanelTerminal?.lwLnbLoFrequencyKHz, (value: string) => value + ' kHz')}
          />
        </div>
      </CollapseGroup>
      <CollapseGroup
        data-test="terminal__status__collapse__group__header--networking__parameters"
        collapseButtonClicked={() => {
          toggleGroupCollapsed('networkingParametersGroupOpened');
        }}
        open={detailsPanelGroupCollapseState.networkingParametersGroupOpened}
        title="Networking Parameters"
      >
        <div className={isOffline ? 'tail__details_faded_wrapper' : ''}>
          <CollapseGroupItem
            id="lwLocalIpAddress"
            label="Local IP"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwLocalIpAddress : null}
          />
          <CollapseGroupItem
            id="lwLocalSubnetMask"
            label="Local Subnet Mask"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwLocalSubnetMask : null}
          />
          <CollapseGroupItem
            id="lwLocalGatewayIpAddress"
            label="Local Gateway"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwLocalGatewayIpAddress : null}
          />
          <GroupDivider />
          <CollapseGroupItem
            id="lwSatelliteIpAddress"
            label="Satellite IP"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwSatelliteIpAddress : null}
          />
          <CollapseGroupItem
            id="lwSatelliteSubnetMask"
            label="Satellite Subnet Mask"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwSatelliteSubnetMask : null}
          />
          <GroupDivider />
          <CollapseGroupItem
            id="lwSatelliteRoutingProtocol"
            label="Routing Protocol"
            value={tailDetailsPanelTerminal ? tailDetailsPanelTerminal.lwSatelliteRoutingProtocol : null}
          />
        </div>
      </CollapseGroup>
    </CollapseGroupScrollbarContainer>
  ) : null;
};

export default TerminalTabDataLinkway;
